import type { HosCoachFocus } from '../../lib/hosCoachFocus';
import { privateChatStorageKey } from '../../lib/privateChatStorage';

interface OfficeThread {
  id: string;
  conversationId?: string;
  title: string;
  lastUser: string;
  lastAssistant: string;
  updatedAt: number;
}

interface SseEvent {
  event: string;
  data: Record<string, unknown>;
}

// One attachment sent alongside a message. Images -> dataUrl (base64), text
// files -> text. Shape mirrors the /api/openclaw/chat backend contract.
export interface OfficeAttachment {
  name?: string;
  mimeType?: string;
  dataUrl?: string;
  text?: string;
}

export interface StreamOfficeEaChatOptions {
  message: string;
  product?: 'bos' | 'hos';
  assistantName?: string;
  focus?: HosCoachFocus;
  attachments?: OfficeAttachment[];
  onAssistantText: (aggregate: string) => void;
  onDone?: () => void;
  onError?: (msg: string) => void;
  signal?: AbortSignal;
}

export function officeAssistantRequestBody(
  opts: StreamOfficeEaChatOptions,
  conversationId?: string,
): Record<string, unknown> {
  const product = opts.product ?? 'bos';
  const assistantName = opts.assistantName?.trim() || (product === 'hos' ? 'Personal Assistant' : 'Executive Assistant');
  return {
    message: opts.message.trim(),
    surface: product,
    assistantName,
    ...(product === 'hos' && opts.focus ? { focus: opts.focus } : {}),
    conversationId,
    newConversation: !conversationId,
    attachments: opts.attachments ?? [],
  };
}

const THREADS_KEY = 'boss_office_threads_v1' as const;
const ACTIVE_THREAD_KEY = 'boss_office_active_thread_v1' as const;
const OFFICE_THREADS_UPDATED_EVENT = 'boss-office-threads-updated';

function threadsKey(product: 'bos' | 'hos'): string | null {
  return privateChatStorageKey(THREADS_KEY, product);
}

function activeThreadKey(product: 'bos' | 'hos'): string | null {
  return privateChatStorageKey(ACTIVE_THREAD_KEY, product);
}

function authHeaders(product: 'bos' | 'hos'): Record<string, string> {
  const token = localStorage.getItem('boss_token') ?? '';
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-BOSS-Product-Surface': product,
  };
}

function loadThreads(product: 'bos' | 'hos'): OfficeThread[] {
  try {
    const key = threadsKey(product);
    if (!key) return [];
    const parsed = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.slice(0, 5) : [];
  } catch {
    return [];
  }
}

function titleFrom(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return 'Office conversation';
  return clean.length > 48 ? `${clean.slice(0, 45)}...` : clean;
}

function dispatchOfficeThreadsUpdated(): void {
  window.dispatchEvent(new Event(OFFICE_THREADS_UPDATED_EVENT));
}

function updateOfficeThread(patch: Partial<OfficeThread>, product: 'bos' | 'hos'): OfficeThread {
  const threads = loadThreads(product);
  const storageThreadsKey = threadsKey(product);
  const storageActiveKey = activeThreadKey(product);
  const activeThreadId = storageActiveKey ? localStorage.getItem(storageActiveKey) || '' : '';
  const activeThread = threads.find((thread) => thread.id === activeThreadId) ?? threads[0];
  const base: OfficeThread = activeThread ?? {
    id: `${product}-${Date.now()}`,
    title: product === 'hos' ? 'Personal conversation' : 'Office conversation',
    lastUser: '',
    lastAssistant: '',
    updatedAt: Date.now(),
  };
  const nextThread = { ...base, ...patch, updatedAt: Date.now() };
  const clipped = [
    nextThread,
    ...threads.filter((thread) => thread.id !== nextThread.id),
  ]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 5);

  if (storageThreadsKey && storageActiveKey) {
    localStorage.setItem(storageThreadsKey, JSON.stringify(clipped));
    localStorage.setItem(storageActiveKey, nextThread.id);
    dispatchOfficeThreadsUpdated();
  }
  return nextThread;
}

function parseSseEvents(chunk: string): SseEvent[] {
  return chunk
    .split('\n\n')
    .map((block) => {
      let event = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) return null;
      try {
        return { event, data: JSON.parse(data) as Record<string, unknown> };
      } catch {
        return null;
      }
    })
    .filter((entry): entry is SseEvent => entry !== null);
}

export async function streamOfficeEaChat(opts: StreamOfficeEaChatOptions): Promise<void> {
  const product = opts.product ?? 'bos';
  const visibleUserText = opts.message.trim();
  let thread = updateOfficeThread({
    title: titleFrom(visibleUserText),
    lastUser: visibleUserText,
  }, product);

  const res = await fetch('/api/openclaw/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(product) },
    body: JSON.stringify(officeAssistantRequestBody(opts, thread.conversationId)),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) throw new Error(`Office request failed (${res.status})`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let aggregate = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const boundary = buffer.lastIndexOf('\n\n');
    if (boundary === -1) continue;
    const complete = buffer.slice(0, boundary + 2);
    buffer = buffer.slice(boundary + 2);
    for (const item of parseSseEvents(complete)) {
      if (item.event === 'conversation' && typeof item.data.conversationId === 'string') {
        thread = updateOfficeThread({ conversationId: item.data.conversationId }, product);
      }
      if (item.event === 'message' && typeof item.data.text === 'string') {
        aggregate += item.data.text;
        opts.onAssistantText(aggregate);
        updateOfficeThread({ conversationId: thread.conversationId, lastAssistant: aggregate }, product);
      }
      if (item.event === 'error') {
        const detail = String(item.data.message ?? item.data.stderrTail ?? 'Office turn failed');
        updateOfficeThread({ lastAssistant: detail }, product);
        opts.onError?.(detail);
        await reader.cancel().catch(() => undefined);
        throw new Error(detail);
      }
    }
  }

  const finalText = aggregate.trim() || 'Done.';
  updateOfficeThread({ conversationId: thread.conversationId, lastAssistant: finalText }, product);
  opts.onDone?.();
}
